// Logistics evidence quick-capture client (D3i). Wraps the sign +
// upload + commit flow into a single uploadLogisticsStopEvidence call
// so the UI hook can fire it as a one-step driver action.
//
// Pattern: mirrors src/lib/opsPaperworkAttachmentsClient.ts (the
// established storage-attachment pattern in this repo) -- POST op=sign
// to /api/logi/stops/evidence to mint a signed Storage upload URL,
// browser uploads the file bytes directly via
// supabase.storage.uploadToSignedUrl (bypasses Vercel's serverless
// body limit), then POST op=commit to verify the object landed +
// stamp the public URL onto the stop's evidence_url +
// evidence_completed_at.
//
// Storage bucket env-keyed server-side as `LOGI_EVIDENCE_BUCKET` —
// see `server/logi-services/evidenceUploadService.ts`
// `evidenceBucketFromEnv()`.
import { manufacturingFetch } from "@/lib/manufacturingClient";
import { requireSupabase } from "@/lib/supabase";
import type { LogiStop } from "@/lib/logistics/types";

type SignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  signed_url: string;
  attachment_id: string;
  content_type: string;
};

type CommitOk = {
  ok: true;
  stop: LogiStop;
  public_url: string;
};

function isSignOk(x: unknown): x is SignOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    o.ok === true &&
    typeof o.bucket === "string" &&
    typeof o.path === "string" &&
    typeof o.token === "string" &&
    typeof o.attachment_id === "string" &&
    typeof o.content_type === "string"
  );
}

function isCommitOk(x: unknown): x is CommitOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return o.ok === true && o.stop !== null && typeof o.stop === "object" && typeof o.public_url === "string";
}

export type EvidenceUploadResult =
  | { ok: true; stop: LogiStop; public_url: string }
  | { ok: false; error: string };

export async function uploadLogisticsStopEvidence(
  stopId: string,
  file: File
): Promise<EvidenceUploadResult> {
  const sb = requireSupabase();
  const contentType = (file.type || "application/octet-stream").toLowerCase();

  const sign = await manufacturingFetch<unknown>("/api/logi/stops/evidence", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "sign",
      stop_id: stopId,
      content_type: contentType,
      byte_size: file.size,
    }),
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  if (!isSignOk(sign.data)) {
    const errCandidate = (sign.data as { error?: unknown })?.error;
    return {
      ok: false,
      error: typeof errCandidate === "string" ? errCandidate : "Sign response was malformed.",
    };
  }
  const s = sign.data;

  const { error: upErr } = await sb.storage.from(s.bucket).uploadToSignedUrl(
    s.path,
    s.token,
    file,
    { contentType: s.content_type, upsert: false }
  );
  if (upErr) return { ok: false, error: upErr.message };

  const commit = await manufacturingFetch<unknown>("/api/logi/stops/evidence", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "commit",
      stop_id: stopId,
      attachment_id: s.attachment_id,
      object_path: s.path,
      content_type: s.content_type,
    }),
  });
  if (!commit.ok) return { ok: false, error: commit.error };
  if (!isCommitOk(commit.data)) {
    const errCandidate = (commit.data as { error?: unknown })?.error;
    return {
      ok: false,
      error: typeof errCandidate === "string" ? errCandidate : "Commit response was malformed.",
    };
  }
  return { ok: true, stop: commit.data.stop, public_url: commit.data.public_url };
}
